<template>
  <PageWrapper :maxWidth="5000" backIcon="arrow-right">
    <div>
      <LoadingWrapper :loading="loading">
        <template #loading>
          <Loading :count="5" />
        </template>
        <template #default>
          <b-row
            v-if="!loading"
            class="margin-0 background-surface margin-top-24 border--solid border--complementary-50 border-radius-8 padding-16"
          >
            <!-- candidate and exercise assessors -->
            <b-col cols="12" class="padding-0">
              <div
                class="table-grid-row margin-bottom-8"
                :style="`gap: 8px; grid-template-columns: repeat(${labels.length}, 1fr); grid-template-columns: ${generateCandidateBoxWidth};`"
              >
                <div
                  class="border--solid border--complementary-50 border-radius-8 padding-x-16 padding-y-4"
                >
                  <AvatarGroup :data="candidateData" :hasProfilePopup="false" />
                </div>
                <div
                  v-for="(exercise, exerciseIndex) in exercisesData"
                  :key="`assessor-${exerciseIndex}`"
                  class="border--solid border--complementary-50 border-radius-8 padding-x-16 padding-y-4"
                >
                  <AvatarGroup
                    :data="generateAssessorData(exercise.assessor)"
                    :hasProfilePopup="false"
                  />
                </div>
              </div>
            </b-col>

            <b-col cols="12" class="padding-0 overflow-x-auto">
              <div
                class="min-width-1200 width--max-content table-grid-row font-size-12 color-complementary-300 margin-bottom-8 min-width-1200"
                :style="`gap: 8px; grid-template-columns: repeat(${labels.length}, 1fr); grid-template-columns: ${generateColumnWidthLabels};`"
              >
                <div
                  v-for="(label, index) in labels"
                  :key="`a-${index}`"
                  class="height-64 flex-start padding-x-16 background-light-gray border-radius-top-8 text--center"
                  :class="
                    !index
                      ? 'background-light-gray'
                      : 'background-double-light-gray'
                  "
                >
                  <span
                    class="font-size-16 color-complementary-400 text-ellipsis--line-2 width-percent-100 text--center"
                    :class="{ 'text--right': !index }"
                  >
                    {{ label }}
                  </span>
                </div>
              </div>
              <div class="min-width-1200 width--max-content">
                <div
                  v-for="(competency, index) in competenciesData"
                  :key="`b-${index}`"
                  class="table-grid-row margin-bottom-8 last-child-border-radius-left-4 border--solid border--complementary-50 border-radius-8 padding-x-16 padding-y-8 width--max-content"
                  :style="`gap: 20px; grid-template-columns: repeat(${labels.length}, 1fr); grid-template-columns: ${generateColumnWidthValues};`"
                >
                  <!-- competency -->
                  <div
                    class="font-size-16 color-complementary-400 border--solid border--complementary-50 border--left border--width-2 padding-left-16 height-percent-100 flex-start"
                  >
                    {{ competency.name }}
                  </div>
                  <!-- exercises -->
                  <div
                    v-for="(exercise, exerciseIndex) in exercisesData"
                    :key="`exercise-${exerciseIndex}`"
                    class="font-size-14 color-complementary-300 text--center flex-center"
                  >
                    <div
                      v-if="
                        exercise.id ===
                        getItem(competency.id, exercise.id, ['exerciseId'])
                      "
                      class="cursor--pointer width-percent-100 height-percent-100 flex-center min-height-44"
                      @click="
                        handleOpenCompetencyScorePin(
                          getItem(competency.id, exercise.id)
                        )
                      "
                    >
                      <div
                        v-if="
                          getItem(competency.id, exercise.id, [
                            'isChangeFinalDescription',
                          ])
                        "
                        class="background-success-50 color-success font-size-16 padding-4 padding-left-16 border-radius-200 height-percent-100"
                      >
                        <span class="margin-left-12">
                          <mdicon name="check-circle" size="32" />
                        </span>
                        {{ getItem(competency.id, exercise.id, ["score"]) }}
                        <span class="margin-right-16">
                          <mdicon name="pencil-outline" size="24" />
                        </span>
                      </div>
                      <div v-else>
                        <span class="font-size-16 color-complementary-400">
                          {{ getItem(competency.id, exercise.id, ["score"]) }}
                        </span>
                        <span
                          class="margin-right-12 color-complementary-100 icon-hover"
                        >
                          <mdicon name="pencil-outline" size="24" />
                        </span>
                      </div>
                    </div>
                    <span
                      class="width-percent-100 height-percent-100 flex-center"
                      v-else
                    >
                      <img :src="emptyColumnImage" />
                    </span>
                  </div>
                  <!-- recommended score -->
                  <div
                    class="cursor--pointer width-percent-100 height-percent-100 flex-center min-height-44"
                  >
                    <span class="font-size-16 color-complementary-400">
                      {{ competency.average }}
                    </span>
                  </div>
                  <!-- conclusion competency -->
                  <div
                    class="cursor--pointer width-percent-100 height-percent-100 flex-center min-height-44"
                    @click="
                      checkAllowCompetencyScore(competency.id)
                        ? handleOpenConclusionPin(competency)
                        : () => {}
                    "
                  >
                    <span class="font-size-16 color-complementary-400">
                      {{ competency.score }}
                    </span>
                    <span
                      class="margin-right-12 color-complementary-100 icon-hover"
                    >
                      <mdicon name="pencil-outline" size="24" />
                    </span>
                  </div>
                </div>
                <!-- conclusion exercises row -->
                <div
                  class="table-grid-row margin-bottom-8 last-child-border-radius-left-4 border--solid border--complementary-50 border-radius-8 padding-x-16 padding-y-8 width--max-content background-complementary-50"
                  :style="`gap: 20px; grid-template-columns: repeat(${labels.length}, 1fr); grid-template-columns: ${generateColumnWidthValues};`"
                >
                  <div
                    class="font-size-16 color-complementary-400 border--solid border--complementary-50 border--left border--width-2 padding-left-16 height-percent-100 flex-start"
                  >
                    {{ $lang.exercisesConclusion }}
                  </div>
                  <div
                    v-for="(exercise, exerciseIndex) in exercisesData"
                    :key="`exercise-${exerciseIndex}`"
                    class="font-size-14 color-complementary-300 text--center flex-center"
                    @click="handleOpenConclusionPin(exercise)"
                  >
                    <div
                      class="cursor--pointer width-percent-100 height-percent-100 flex-center min-height-44"
                    >
                      <span class="font-size-16 color-complementary-400">
                        {{ exercise.score }}
                      </span>
                      <span
                        class="margin-right-12 color-complementary-100 icon-hover"
                      >
                        <mdicon name="pencil-outline" size="24" />
                      </span>
                    </div>
                  </div>
                  <span
                    class="width-percent-100 height-percent-100 flex-center"
                  >
                    <img
                      :src="emptyColumnImage"
                      class="width-percent-100 height-percent-100"
                    />
                  </span>
                  <span
                    class="width-percent-100 height-percent-100 flex-center"
                  >
                    <img
                      :src="emptyColumnImage"
                      class="width-percent-100 height-percent-100"
                    />
                  </span>
                </div>
              </div>
            </b-col>
          </b-row>
        </template>
      </LoadingWrapper>
      <CompetencyScorePin
        :visible="isCompetencyScorePin"
        :item="currentItem"
        @handleClose="handleCloseCompetencyScorePin"
        @handleReload="fetchData"
      />
      <CompetencyConclusionPin
        :visible="isConclusionPin"
        :item="currentItem"
        @handleClose="handleCloseConclusionPin"
        @handleReload="fetchData"
      />
    </div>
  </PageWrapper>
</template>

<script>
import {
  PageWrapper,
  AvatarGroup,
  LoadingWrapper,
} from "@/components/re-design";
import Loading from "./loading/Loading.vue";
import CompetencyScorePin from "./components/CompetencyScorePin.vue";
import CompetencyConclusionPin from "./components/CompetencyConclusionPin.vue";
import { computed, ref } from "vue";
import { pathOr, includes } from "ramda";
import {
  generateConfig,
  generateCurrentRoute,
  generateError,
  generateLang,
  setUniqueArrayOfObjects,
  toastAlert,
} from "@/shared/utils";
import { getCandidateOverview } from "@/modules/kanoon/services";

export default {
  components: {
    PageWrapper,
    AvatarGroup,
    CompetencyScorePin,
    CompetencyConclusionPin,
    LoadingWrapper,
    Loading,
  },
  setup() {
    const emptyColumnImage = generateConfig([
      "images",
      "shared",
      "redesign",
      "emptyColumn",
    ]);

    // generate table data
    const generateCandidateData = (candidate) => {
      const candidateName =
        pathOr("", ["person", "full_name"], candidate) || "**********";
      const candidateNumber = pathOr(
        "",
        ["person_center_execute_code"],
        candidate
      );
      return {
        type: generateLang(["candidate"]),
        id: candidateNumber,
        name: candidateName,
        images: [pathOr("", ["person", "img_thumb"], candidate)],
      };
    };
    const generateAssessorData = (assessor) => {
      const name =
        pathOr("", ["person", "full_name"], assessor) || "**********";
      const number = pathOr("", ["person_center_execute_code"], assessor);
      return {
        type: generateLang(["assessor"]),
        id: number,
        name,
        images: [pathOr("", ["person", "img_thumb"], assessor)],
      };
    };
    const generateLabels = (items) => {
      // get all exercises and remove null exercises
      const exercises = items.map(({ exercise }) => exercise).filter(Boolean);
      // remove repeated exercises
      const uniqueExercises = setUniqueArrayOfObjects(exercises, "id");
      // get exercises name
      const exercisesName = uniqueExercises.map(({ name }) => name);

      const competencies = generateLang(["competencies"]);
      const recommendedScore = generateLang(["recommendedScore"]);
      const competenciesConclusion = generateLang(["competenciesConclusion"]);
      return [
        competencies,
        ...exercisesName,
        recommendedScore,
        competenciesConclusion,
      ];
    };

    // fetching data
    const centerExecuteId = generateCurrentRoute(["params", "centerExecuteId"]);
    const candidateId = generateCurrentRoute(["params", "candidateId"]);
    const userExecutiveRoleId = ref(null);
    const candidateData = ref(null);
    const labels = ref([]);
    const itemsData = ref([]);
    const exercisesData = ref([]);
    const competenciesData = ref([]);
    const loading = ref(false);
    const fetchData = async () => {
      try {
        loading.value = true;
        const response = await getCandidateOverview(
          centerExecuteId,
          candidateId
        );
        const { executiveRoleId, candidate, items, exercises, competencies } =
          response;
        userExecutiveRoleId.value = executiveRoleId;
        candidateData.value = generateCandidateData(candidate);
        labels.value = generateLabels(items);
        itemsData.value = items;
        exercisesData.value = exercises;
        competenciesData.value = competencies;
      } catch (error) {
        generateError(error);
      } finally {
        loading.value = false;
      }
    };
    fetchData();

    // generate candidate box with
    const generateCandidateBoxWidth = computed(() =>
      labels.value.map(() => "max-content").join(" ")
    );
    // generate table columns with
    const generateColumnWidthLabels = computed(() =>
      labels.value
        .map((item, index) => {
          const customColumns = includes(index, [
            labels.value.length - 1,
            labels.value.length - 2,
          ]);
          if (exercisesData.value.length <= 2) {
            return customColumns ? "139px" : "300px";
          }
          if (exercisesData.value.length <= 3) {
            return customColumns ? "139px" : "250px";
          }
          return customColumns ? "139px" : "200px";
        })
        .join(" ")
    );
    const generateColumnWidthValues = computed(() =>
      labels.value
        .map((item, index) => {
          const customColumns = includes(index, [
            labels.value.length - 1,
            labels.value.length - 2,
          ]);
          if (exercisesData.value.length <= 2) {
            return customColumns ? "116px" : "288px";
          }
          if (exercisesData.value.length <= 3) {
            return customColumns ? "116px" : "238px";
          }
          return customColumns ? "116px" : "188px";
        })
        .join(" ")
    );

    // handle competency score modal
    const currentItem = ref(null);
    const generateCurrentItem = (item) => {
      currentItem.value = {
        userExecutiveRoleId: userExecutiveRoleId.value,
        centerExecuteId,
        candidateId,
        ...item,
      };
    };
    const isCompetencyScorePin = ref(false);
    const handleOpenCompetencyScorePin = (item) => {
      if (!item) return;
      generateCurrentItem(item);
      isCompetencyScorePin.value = true;
    };
    const handleCloseCompetencyScorePin = () =>
      (isCompetencyScorePin.value = false);

    // handle competency or exercise conclusion score modal
    const isConclusionPin = ref(false);
    const handleOpenConclusionPin = (item) => {
      if (!item) return;
      generateCurrentItem(item);
      isConclusionPin.value = true;
    };
    const handleCloseConclusionPin = () => (isConclusionPin.value = false);

    const getItem = (competencyId, exerciseId, keys = []) => {
      const item = itemsData.value.find(
        (item) =>
          item.competencyId === competencyId && item.exerciseId === exerciseId
      );
      return keys.length ? pathOr(null, keys, item) : item;
    };

    const checkAllowCompetencyScore = (competencyId) => {
      const data = itemsData.value.filter(
        (item) => item.competencyId === competencyId
      );

      const allowScore = data.every(({ score }) => score !== null);

      if (!allowScore) {
        toastAlert(
          generateLang(["notAllowedCompetencyScoreInWashup"]),
          "warning"
        );
      }

      return allowScore;
    };

    return {
      candidateData,
      generateAssessorData,
      loading,
      labels,
      itemsData,
      exercisesData,
      competenciesData,
      generateCandidateBoxWidth,
      generateColumnWidthLabels,
      generateColumnWidthValues,
      pathOr,
      emptyColumnImage,
      currentItem,
      isCompetencyScorePin,
      handleOpenCompetencyScorePin,
      handleCloseCompetencyScorePin,
      isConclusionPin,
      handleOpenConclusionPin,
      handleCloseConclusionPin,
      fetchData,
      getItem,
      checkAllowCompetencyScore,
    };
  },
};
</script>
<style lang="scss">
.table-grid-row {
  display: grid;
  grid-template-rows: auto;
  align-items: start;
}

.border-radius-left-4 {
  border-radius: 4px 0 0 4px;
}
.last-child-border-radius-left-4 {
  span:last-child {
    @extend .border-radius-left-4;
  }
}
</style>
